import React, {
  useRef,
  useEffect,
  useCallback,
  useState
} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import AppTourWelcomeModal from '../shared/molecule/onboarding/AppTourWelcomeModal';
import AppTourChecklist from '../shared/molecule/onboarding/AppTourChecklist';
import {DashBoardAppTour} from '../Onboarding/DashBoardAppTour';
import {ProfileAppTour} from '../Onboarding/ProfileAppTour';
import {dashboardTourSteps, profileTourSteps, subscribedTourSteps, InvitedUserTourSteps} from '../../utils/steps';
import {FetchProfile, PostProfile} from "@meprism/shared/src/redux/profile/ProfileSlice";
import {useAppDispatch, useAppSelector} from '../../redux/storeExports';
import {TourState, ProfileSlice} from '@meprism/shared/src/redux/profile/ProfileTypes';
import {selectActiveGrant} from '../../redux/product/ProductSlice'
import {SubscribedAppTour} from '../Onboarding/SubscribedAppTour';
import EndTourModal from '../shared/molecule/onboarding/EndTourModal';
import { Step } from 'react-joyride';
import AnalyticsManager from '../../services/AnalyticsManager';
import { MPTourEventType } from '../../services/AnalyticsEvents';
interface OnboardingWrapperProps {
  children: React.ReactNode;
}

enum FreeAppTourSteps {
  None,
  Welcome,
  DashboardTour,
  ProfileTour,
  Checklist,
  PurchaseSubscription,
}

enum SubscribedAppTourSteps {
  None,
  Welcome,
  SubscriptionTour,
}

const checklistSteps = [
  'Introduction to the Privacy Dashboard.',
  'How to improve profile scans.',
  'Save and scan your detailed profile!',
  'Complete the onboarding.'
];

const steps = checklistSteps.map((label) => ({label}));

const AppTourWrapper: React.FC<OnboardingWrapperProps> = ({children}) => {
  const [freeAppTourSteps, setFreeAppTourSteps] = useState<FreeAppTourSteps>(FreeAppTourSteps.None);
  const [subscribedAppTourSteps, setSubscribedAppTourSteps] = useState<SubscribedAppTourSteps>(SubscribedAppTourSteps.None);
  const completedStepsRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);
  const [profile, setProfile] = useState<ProfileSlice | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch()
  const grant = useAppSelector(selectActiveGrant);
  const [subscribedTourConfig, setSubscribedTourConfig] = useState({
    isInvitedUser: false,
    subscribeTourSteps: [] as Step[],
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const fetchedProfile = await dispatch(FetchProfile()).unwrap();
        setProfile(fetchedProfile);
        const { tour_state: tourState, address, auth_letter_s3_key: authLetterS3Key } = fetchedProfile || {};
        const { free_user_tour_state: freeUserTourState, subscribed_user_tour_state: subscribedUserTourState } = tourState || {};
        const showSubscribedTour = address?.some(addr => addr?.street1?.trim()) && !!authLetterS3Key
        if (!grant && freeUserTourState === 'started') {
          timeoutRef.current = window.setTimeout(() => {
            setFreeAppTourSteps(FreeAppTourSteps.PurchaseSubscription);
          }, 800);
        } else if (location.pathname === '/dashboard' && !location.state?.fromTour) {
          if (!grant && !freeUserTourState) {
            timeoutRef.current = window.setTimeout(() => {
              setFreeAppTourSteps(FreeAppTourSteps.Welcome);
            }, 800);
          }
          else if(grant && !subscribedUserTourState && !freeUserTourState && showSubscribedTour) {
            setSubscribedTourConfig({
              isInvitedUser: true,
              subscribeTourSteps: InvitedUserTourSteps,
            });
            timeoutRef.current = window.setTimeout(() => {
              setSubscribedAppTourSteps(SubscribedAppTourSteps.Welcome);
            }, 800);
          }
          else if (grant && !subscribedUserTourState && showSubscribedTour) {
            setSubscribedTourConfig({
              isInvitedUser: false,
              subscribeTourSteps: subscribedTourSteps,
            });
            timeoutRef.current = window.setTimeout(() => {
              setSubscribedAppTourSteps(SubscribedAppTourSteps.Welcome);
            }, 800);
          }
        }
      } catch (error) {
        console.error('Failed to fetch profile', error);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleUpdateTourState = useCallback(async (state: TourState, mpTourEvent: MPTourEventType) => {
    if (!profile) return;
    AnalyticsManager.trackTypedEvent(mpTourEvent)
    await dispatch(PostProfile({
      ...profile,
      isOnBoardingRequest: profile.isOnBoardingRequest ?? undefined,
      additional_information: typeof profile.additional_information === 'object' && profile.additional_information !== null
        ? profile.additional_information
        : undefined,
      tour_state: {
        ...profile.tour_state || {},
        ...state,
      },
      last_scan_initiated_at: profile.last_scan_initiated_at,
      metadata: profile.metadata,
    }));
  }, [dispatch, profile]);

  const handleStartFreeTour = useCallback(async () => {

    await handleUpdateTourState({
      free_user_tour_state: 'started',
    },
    {
      event: "App Tour Started - Free",
      source: 'Web App'
    }
  );

    setFreeAppTourSteps(FreeAppTourSteps.DashboardTour);
  }, [handleUpdateTourState]);


  const handleSkipFreeTour = useCallback(async () => {
    setFreeAppTourSteps(FreeAppTourSteps.None);
    await handleUpdateTourState({
      free_user_tour_state: 'skipped',
    },
    {
      event: "App Tour Skipped - Free",
      source: 'Web App'
    }
  );
  }, [handleUpdateTourState]);

  const handleStartSubscribedTour = useCallback(async () => {
    await handleUpdateTourState({
      subscribed_user_tour_state: 'started',
    },
    {
      event: "App Tour Started - Subscribed",
      source: 'Web App'
    }
  );
    if (!subscribedTourConfig.isInvitedUser) {
      navigate('/dashboard/profile');
    }
    setSubscribedAppTourSteps(SubscribedAppTourSteps.SubscriptionTour);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUpdateTourState]);

  const handleSkipSubscribedTour = useCallback(async () => {
    setSubscribedAppTourSteps(SubscribedAppTourSteps.None);
    await handleUpdateTourState({
      subscribed_user_tour_state: 'skipped',
    },
    {
        event: "App Tour Skipped - Subscribed",
        source: 'Web App'
    }
    );
  }, [handleUpdateTourState]);

  const handleChecklistClose = useCallback(() => {
    setFreeAppTourSteps(FreeAppTourSteps.None);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDashboardTourComplete = useCallback(() => {
    setFreeAppTourSteps(FreeAppTourSteps.Checklist);
    timeoutRef.current = window.setTimeout(() => {
      setFreeAppTourSteps(FreeAppTourSteps.ProfileTour);
      navigate('/dashboard/profile');
    }, 3000);
  }, [navigate]);

  const handleProfileTourComplete = useCallback(() => {
    setFreeAppTourSteps(FreeAppTourSteps.Checklist);
    timeoutRef.current = window.setTimeout(() => {
      setFreeAppTourSteps(FreeAppTourSteps.None);
      completedStepsRef.current = 0;
    }, 3000);

    timeoutRef.current = window.setTimeout(() => {
      setFreeAppTourSteps(FreeAppTourSteps.PurchaseSubscription);
    }, 60000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgradePromptClose = useCallback(async () => {
    setFreeAppTourSteps(FreeAppTourSteps.None);
    await handleUpdateTourState({
      free_user_tour_state: 'completed',
    },
    {
      event: "App Tour Finished - Free",
      source: 'Web App'
    }
  );
  }, [handleUpdateTourState]);

  const handleUpgrade = useCallback(async () => {
    setFreeAppTourSteps(FreeAppTourSteps.None);
    await handleUpdateTourState({
      free_user_tour_state: 'completed',
    },
    {
      event: "App Tour Finished - Free",
      source: 'Web App'
    });
    navigate('/subscription');

  }, [navigate, handleUpdateTourState]);

  // const handleUpgradePromoClose = useCallback(async () => {
  //   setFreeAppTourSteps(FreeAppTourSteps.None);
  //   await handleUpdateTourState({
  //     free_user_tour_state: 'completed',
  //   });
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [handleUpdateTourState]);

  return (
    <>
      {freeAppTourSteps === FreeAppTourSteps.Welcome && (
        <AppTourWelcomeModal
          open
          startButtonLabel="START A QUICK TOUR"
          title={
            <>
              Welcome!
              <br/>
              We&apos;re glad you&apos;re here.
            </>
          }
          description=" To get a feel of the place, take our guided tour where we&apos;ll get you set up and show you how to start protecting your privacy."
          onStart={handleStartFreeTour}
          onSkip={handleSkipFreeTour}
        />
      )}

      {freeAppTourSteps === FreeAppTourSteps.DashboardTour && (
        <DashBoardAppTour
          steps={dashboardTourSteps}
          modalTitle="This is the Extended Coverage Tab"
          modalDescription="These are non-scannable data brokers. You may receive email communications acknowledging the data removal request. This is normal and indicates Priwall is working on your behalf behind the scenes."
          modalActionLabel="Continue"
          completedStepsRef={completedStepsRef}
          onModalAction={handleDashboardTourComplete}
        />
      )}

      {freeAppTourSteps === FreeAppTourSteps.ProfileTour && (
        <ProfileAppTour
          steps={profileTourSteps}
          completedStepsRef={completedStepsRef}
          modalDescription="You can click into each data broker to see the data they hold on you, such as name, city, state and relatives' information. If you'd like to remove your information from these data brokers, make sure to subscribe to a Premium account to start protecting your privacy."
          modalActionLabel="Continue"
          onModalAction={handleProfileTourComplete}
        />
      )}

      {freeAppTourSteps === FreeAppTourSteps.Checklist && (
        <AppTourChecklist
          open
          progress={(completedStepsRef.current * 100) / checklistSteps.length}
          steps={steps}
          onClose={handleChecklistClose}
        />
      )}

      {freeAppTourSteps === FreeAppTourSteps.PurchaseSubscription && (
        <EndTourModal
          open
          onClose={handleUpgradePromptClose}
          onUpgrade={handleUpgrade}
          buttonText='Upgrade'
          title='But wait! This is where the real protection starts.'
          description='Upgrade to take your info down and keep it down, with ongoing monitoring.'
          keepFreeAccountLabel='No thanks, I&apos;ll keep my free account'
        />
      )}

      {/* {freeAppTourSteps === FreeAppTourSteps.UpgradePromo && (
        <PromotionalAppTourModal
          open
          onClose={handleUpgradePromoClose}
          onUpgrade={handleUpgrade}
          videoUrl="https://www.youtube.com/watch?v=s0EpaxqNJ58"
        />
      )} */}

      {subscribedAppTourSteps === SubscribedAppTourSteps.Welcome && (
        <AppTourWelcomeModal
          open
          startButtonLabel="START THE TOUR"
          title={
            <>
              Welcome!
              <br/>
              Thank you for subscribing!
            </>
          }
          description="You now have access to powerful privacy tools and full data control. Let’s take a quick tour to get you started."
          onStart={handleStartSubscribedTour}
          onSkip={handleSkipSubscribedTour}
        />
      )}
      {subscribedAppTourSteps === SubscribedAppTourSteps.SubscriptionTour && (
        <SubscribedAppTour
          steps={subscribedTourConfig.subscribeTourSteps}
          handleUpdateTourState={handleUpdateTourState}
        />
      )}
      {children}
    </>
  );
};

export default AppTourWrapper;